@if (alterationDetails() && reservations()) {
  <div class="grid gap-6">
    <div class="flex items-center gap-2">
      <h2 class="m-0">Reservation details</h2>
    </div>

    <div class="grid">
      <label class="form-label">Check-in / Check-out</label>
      <div class="mt-2 grid border rounded-lg gap-2">
        <ui-date-picker
          [rangeSelect]="true"
          [initialDateRange]="[alterationRequest()?.start_date, alterationRequest()?.end_date]"
          [reservations]="reservations()"
          (datesSelected)="datesSelected($event)"></ui-date-picker>

        @if (datesChanged()) {
          <div class="text-sm text-night-lighter text-center mb-2">
            Original:
            {{ alterationDetails().previous_state.checkin | date: 'fullDate' }} -
            {{ alterationDetails().previous_state.checkout | date: 'fullDate' }} ({{
              alterationDetails().previous_state.checkin | numNights: alterationDetails().previous_state.checkout
            }}
            night stay)
          </div>
        }
      </div>
    </div>

    <div class="grid grid-cols-4 gap-3">
      <div class="gap">
        <label class="form-label" for="adults">Adults</label>
        <mat-form-field class="fw">
          <input
            name="adults"
            [value]="alterationRequest().number_of_adults"
            (input)="updateGuestCount({ key: 'number_of_adults', value: $event.target.value })"
            type="number"
            matInput
            min="1"
            [max]="alterationDetails().property.person_capacity - alterationRequest().number_of_children" />

          @if (adultsChanged()) {
            <mat-hint> Original: {{ alterationDetails().previous_state.num_guests.adults }} </mat-hint>
          }
        </mat-form-field>
      </div>

      <div class="gap">
        <label class="form-label" for="children">Children</label>

        <mat-form-field class="fw">
          <input
            name="children"
            [value]="alterationRequest().number_of_children"
            (input)="updateGuestCount({ key: 'number_of_children', value: $event.target.value })"
            type="number"
            matInput
            [max]="alterationDetails().property.person_capacity - alterationRequest().number_of_adults" />
          @if (childrenChanged()) {
            <mat-hint> Original: {{ alterationDetails().previous_state.num_guests.children }} </mat-hint>
          }
        </mat-form-field>
      </div>

      <div class="gap">
        <label class="form-label" for="infants">Infants</label>

        <mat-form-field class="fw">
          <input
            name="infants"
            [value]="alterationRequest().number_of_infants"
            (input)="updateGuestCount({ key: 'number_of_infants', value: $event.target.value })"
            type="number"
            matInput
            max="5" />
          @if (infantsChanged()) {
            <mat-hint> Original: {{ alterationDetails().previous_state.num_guests.infants }} </mat-hint>
          }
        </mat-form-field>
      </div>

      <div class="gap">
        <label class="form-label" for="pets">Pets</label>

        <mat-form-field class="fw">
          <input
            name="pets"
            [value]="alterationRequest().number_of_pets"
            (input)="updateGuestCount({ key: 'number_of_pets', value: $event.target.value })"
            type="number"
            matInput
            max="5" />
          @if (petsChanged()) {
            <mat-hint> Original: {{ alterationDetails().previous_state.num_guests.pets }} </mat-hint>
          }
        </mat-form-field>
      </div>
    </div>

    @if (
      alterationRequest().number_of_adults + alterationRequest().number_of_children >
      alterationDetails().property.person_capacity
    ) {
      <span
        class="block text-sm text-night-lighter text-center"
        style="padding-bottom: 12px; padding-top: 12px; font-size: 14px; text-align: center">
        This property only allows a maximum of
        {{ alterationDetails().property.person_capacity }} guests, not including infants.
      </span>
    }

    @if (fetchingQuote()) {
      <ui-loading-spinner size="24" />
    }

    @if (quoteError() && !fetchingQuote()) {
      <ui-alert
        type="inline"
        color="error"
        icon="griddy-info-circle"
        [title]="'Unable to generate a quote'"
        [hideCloseButton]="true"
        [message]="quoteError()" />
    }

    <!-- Pricing table -->
    <table class="w-full table-auto text-sm border border-gray-200 rounded-[20px] px-4 py-2 border-spacing-x-3">
      <thead class="border-b border-gray-200">
        <tr class="border-b border-gray-100">
          <th class="text-left py-2 font-semibold">Item</th>
          <th class="text-right py-2 font-semibold w-32">Original</th>
          <th class="text-right py-2 font-semibold w-32">New</th>
        </tr>
      </thead>
      <tbody>
        @for (line of priceBreakdownLines(); track line.title) {
          <tr class="border-b border-gray-100">
            <td class="py-2">
              <div class="flex flex-col gap-1">
                <span>{{ line.title }}</span>
                @if (line.canRemove && !line.isRemoved) {
                  <ui-button
                    size="sm"
                    variant="link-gray"
                    [label]="'Remove'"
                    (onClick)="removeLine(line.key)"
                    class="self-start" />
                }
              </div>
            </td>
            <td class="py-2 font-mono text-right w-32">{{ line.old }}</td>
            <td class="py-2 font-mono text-right w-32">
              @if (line.canEdit && !line.isRemoved) {
                <ui-form-field
                  label=""
                  [placeholder]="'0.00'"
                  [fieldType]="'text'"
                  [ngModel]="line.newRawValue"
                  (ngModelChange)="updatePricing(line.key, $event)"
                  [prefix]="alterationDetails().current_state.total.currency | currencyCode" />
              } @else {
                {{ line.new }}
              }
            </td>
          </tr>
        }
      </tbody>
    </table>

    @if (fetchingQuote()) {
      <ui-loading-spinner size="24" />
    } @else {
      @if (!quoteError()) {
        <div class="border border-gray-200 rounded-[20px] px-5 py-2">
          <div>
            <div class="*:min-h-14 *:flex *:items-center">
              <div class="flex justify-between border-b border-gray-200">
                <div class="font-semibold">Original price</div>
                <div class="font-mono">{{ alterationDetails()!.previous_state.total.formatted_string }}</div>
              </div>

              <div class="flex justify-between border-b border-gray-200">
                <div class="font-semibold">New price</div>
                <div class="font-mono">{{ alterationDetails()!.current_state.total.formatted_string }}</div>
              </div>

              <div class="flex justify-between">
                <div>
                  <div class="font-semibold">Price difference</div>
                </div>

                <div class="font-mono font-semibold">
                  {{ alterationDetails()!.financial_actions?.changed_splits?.total?.formatted_string }}
                </div>
              </div>
            </div>
          </div>
        </div>
      } @else {
        <ui-alert
          type="inline"
          color="error"
          icon="griddy-info-circle"
          [title]="'Unable to generate a quote'"
          [hideCloseButton]="true"
          [message]="quoteError()" />
      }
    }
  </div>
} @else {
  <ui-loading-spinner />
}
